import { useEffect, useState, Fragment, useRef } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { DownloadTableExcel } from "react-export-table-to-excel";
import { Button, Spinner, Alert } from "react-bootstrap";

const ReceiptReportTable = ({ formData }) => {
  const tableRef = useRef(null);
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleString().replace(/[/:]/g, "_");
  const [receiptReportData, setReceiptReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchData = async (page = 1, append = false) => {
    const isInitialLoad = page === 1;
    if (isInitialLoad) {
      setLoading(true);
      setError(null);
    } else {
      setLoadingMore(true);
    }

    try {
      const fromDateStr = formData.fromDate.toISOString().split("T")[0];
      const toDateStr = formData.toDate.toISOString().split("T")[0];

      // Add timeout for large date ranges
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout for pagination

      const response = await axios.get(
        `${API_BASE_URL}/api/getReceiptReportData?fromDate=${fromDateStr}&toDate=${toDateStr}&page=${page}&limit=1000`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      const { data, total, hasMore: moreData, currentLimit } = response.data;

      if (append) {
        setReceiptReportData((prev) => [...prev, ...data]);
      } else {
        setReceiptReportData(data);
      }

      setTotalRecords(total);
      setHasMore(moreData);
      setCurrentPage(page);
    } catch (error) {
      console.log(error);
      if (error.name === "AbortError") {
        setError(
          "Request timed out. Please try with a smaller date range or use pagination."
        );
      } else {
        setError("Failed to load data. Please try again.");
      }
      if (!append) {
        setReceiptReportData([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    setReceiptReportData([]);
    fetchData(1, false);
  }, [formData]);

  const loadMoreData = () => {
    if (!loadingMore && hasMore) {
      fetchData(currentPage + 1, true);
    }
  };

  const totalSum = receiptReportData.reduce(
    (acc, row) => {
      acc.amount += row.amount;
      return acc;
    },
    {
      amount: 0,
    }
  );

  return (
    <Fragment>
      <DownloadTableExcel
        filename={"ReceiptReport_" + formattedDate}
        sheet="Receipt_Report"
        currentTableRef={tableRef.current}
      >
        <Button disabled={loading}> Export excel </Button>
      </DownloadTableExcel>

      {error && (
        <Alert variant="danger" className="mt-3">
          {error}
        </Alert>
      )}

      {totalRecords > 0 && (
        <Alert variant="info" className="mt-3">
          Showing {receiptReportData.length} of {totalRecords} records.
          {hasMore && " Scroll down to load more data."}
        </Alert>
      )}

      <div className="table-responsive">
        <table
          ref={tableRef}
          className="table table-bordered table-hover table-striped table-info mt-3"
        >
          <thead>
            <tr>
              <th>Invoice No</th>
              <th>Date</th>
              <th>Amount</th>
              <th>MOR</th>
              <th>Ledger</th>
              <th>Outstanding Balance</th>
              <th>Narration</th>
            </tr>
          </thead>
          {loading && receiptReportData.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={7} className="text-center py-4">
                  <Spinner animation="border" role="status" className="me-2">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  Loading receipt data...
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {receiptReportData.length === 0 && !loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-4">
                    No data found for the selected date range.
                  </td>
                </tr>
              ) : (
                <>
                  {receiptReportData.map((row, index) => (
                    <tr key={`${row.invoice_no}-${index}`}>
                      <td>{row.invoice_no}</td>
                      <td>{row.invoice_date}</td>
                      <td>{row.amount}</td>
                      <td>{row.mor}</td>
                      <td>{row.ledger}</td>
                      <td>{row.outstanding_balance}</td>
                      <td>{row.narration}</td>
                    </tr>
                  ))}
                  {loadingMore && (
                    <tr>
                      <td colSpan={7} className="text-center py-2">
                        <Spinner
                          animation="border"
                          size="sm"
                          role="status"
                          className="me-2"
                        >
                          <span className="visually-hidden">
                            Loading more...
                          </span>
                        </Spinner>
                        Loading more data...
                      </td>
                    </tr>
                  )}
                </>
              )}
              {receiptReportData.length > 0 && (
                <tr>
                  <th colSpan={2}>Total</th>
                  <th>{totalSum.amount.toFixed(2)}</th>
                  <th colSpan={4}></th>
                </tr>
              )}
            </tbody>
          )}
        </table>
      </div>

      {/* Load More Button outside table for better visibility */}
      {hasMore && !loadingMore && receiptReportData.length > 0 && (
        <div className="text-center mt-3 mb-3">
          <Button
            variant="outline-primary"
            size="lg"
            onClick={loadMoreData}
            className="px-4"
          >
            Load More Data
          </Button>
        </div>
      )}
    </Fragment>
  );
};

export default ReceiptReportTable;
